import type {
  CartaoMetrica,
  Comparacao,
  DiagnosticoFadiga,
  FaixaScore,
  MetricasDerivadas,
  ResultadoSaude,
  ResultadoScore,
  Tendencia,
} from '@baiocchi/shared'
import { Cartao, Dica, Etiqueta, type TomEtiqueta } from '@baiocchi/ui'

import { IconeAlerta, IconeCaindo, IconeEstavel, IconeSubindo } from '../../componentes/icones'
import { formatarNumero } from '../../utilitarios/formatar'

/**
 * Peças visuais do Command Center.
 *
 * Ficam num arquivo só porque as sete telas de `/meta-ads` mostram os mesmos objetos —
 * score, tendência, métrica, fadiga. Sete implementações de "badge de score" divergiriam
 * na primeira mudança de faixa, e o gestor veria a mesma nota em duas cores diferentes em
 * duas telas.
 *
 * Nenhuma delas decide nada: cor, faixa e frase vêm calculadas do servidor. É a mesma
 * fronteira que `packages/ui` estabelece — regra de negócio não mora no componente.
 */

// ---------------------------------------------------------------------------
// Formatação de métrica
// ---------------------------------------------------------------------------

/**
 * Formata pelo `formato` que o contrato declarou, não por adivinhação do nome do campo.
 *
 * **`null` nunca vira zero.** Um CPL indefinido (zero leads) mostrado como "R$ 0,00" diria
 * "lead de graça" exatamente no anúncio que gastou R$ 400 sem lead nenhum — a inversão
 * mais perigosa que esta tela pode cometer. `null` vira `—`, que é a verdade.
 */
export function formatarMetrica(
  valor: number | null,
  formato: CartaoMetrica['formato'],
): string {
  if (valor === null || !Number.isFinite(valor)) return '—'

  switch (formato) {
    case 'MOEDA':
      return valor.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
      })
    case 'PERCENTUAL':
      return `${valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
    case 'DECIMAL':
      return valor.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    case 'INTEIRO':
      return formatarNumero(Math.round(valor))
  }
}

/** As razões principais de uma linha, no formato compacto da tabela. */
export function resumirMetricas(metricas: MetricasDerivadas): ReadonlyArray<{
  readonly rotulo: string
  readonly valor: string
}> {
  return [
    { rotulo: 'Investimento', valor: formatarMetrica(metricas.gasto, 'MOEDA') },
    { rotulo: 'Leads', valor: formatarMetrica(metricas.leads, 'INTEIRO') },
    { rotulo: 'CPL', valor: formatarMetrica(metricas.cpl, 'MOEDA') },
    { rotulo: 'CTR', valor: formatarMetrica(metricas.ctr, 'PERCENTUAL') },
    { rotulo: 'CPC', valor: formatarMetrica(metricas.cpc, 'MOEDA') },
    { rotulo: 'Frequência', valor: formatarMetrica(metricas.frequencia, 'DECIMAL') },
  ]
}

// ---------------------------------------------------------------------------
// Score
// ---------------------------------------------------------------------------

const TOM_POR_FAIXA: Readonly<Record<FaixaScore, TomEtiqueta>> = {
  EXCELENTE: 'sucesso',
  ATENCAO: 'atencao',
  CRITICO: 'perigo',
  SEM_DADOS: 'neutro',
}

const ROTULO_POR_FAIXA: Readonly<Record<FaixaScore, string>> = {
  EXCELENTE: 'Excelente',
  ATENCAO: 'Atenção',
  CRITICO: 'Crítico',
  SEM_DADOS: 'Sem dados',
}

/**
 * Nota com as parcelas na dica — o "por que recebeu essa nota?" do §7.
 *
 * As parcelas ficam numa `Dica` e não expandidas na tabela porque são 3 a 6 linhas por
 * anúncio: expandidas, uma lista de 30 anúncios viraria 150 linhas de texto e a tabela
 * deixaria de ser legível. Quem quer o detalhe passa o mouse; quem quer o panorama vê as
 * notas.
 */
export function BadgeScore({ score }: { readonly score: ResultadoScore }) {
  if (!score.avaliavel) {
    return (
      <Dica texto="Volume insuficiente no período para avaliar. Não é nota baixa — é ausência de amostra.">
        <Etiqueta tom="neutro">Sem dados</Etiqueta>
      </Dica>
    )
  }

  const explicacao =
    score.parcelas.length === 0
      ? 'Desempenho dentro do padrão da conta, sem desvio relevante em nenhum critério.'
      : score.parcelas
          .map((parcela) => `${parcela.delta > 0 ? '+' : ''}${parcela.delta} ${parcela.motivo}`)
          .join('\n')

  return (
    <Dica texto={explicacao}>
      <Etiqueta tom={TOM_POR_FAIXA[score.faixa]}>
        {score.nota}/100 · {ROTULO_POR_FAIXA[score.faixa]}
      </Etiqueta>
    </Dica>
  )
}

// ---------------------------------------------------------------------------
// Tendência e comparação
// ---------------------------------------------------------------------------

/**
 * Seta de tendência do CPL.
 *
 * `menorMelhor` está fixo em CPL: **queda é boa**. É o único lugar da tela onde a direção
 * e o julgamento apontam para lados opostos, e por isso o rótulo diz "melhorando" em vez
 * de "caindo" — "CPL caindo 🔴" faria o gestor ler o vermelho e não o texto.
 */
export function SetaTendencia({ tendencia }: { readonly tendencia: Tendencia }) {
  switch (tendencia) {
    case 'CAINDO':
      return (
        <Dica texto="Custo por lead em queda no período — está melhorando.">
          <span className="inline-flex items-center gap-1 text-sucesso">
            <IconeCaindo className="h-4 w-4" />
            <span className="text-xs">melhorando</span>
          </span>
        </Dica>
      )
    case 'SUBINDO':
      return (
        <Dica texto="Custo por lead em alta no período — está piorando.">
          <span className="inline-flex items-center gap-1 text-perigo">
            <IconeSubindo className="h-4 w-4" />
            <span className="text-xs">piorando</span>
          </span>
        </Dica>
      )
    case 'ESTAVEL':
      return (
        <span className="inline-flex items-center gap-1 text-conteudo-suave">
          <IconeEstavel className="h-4 w-4" />
          <span className="text-xs">estável</span>
        </span>
      )
    default:
      return (
        <Dica texto="Período curto demais para medir tendência (mínimo de 4 dias com dados).">
          <span className="text-xs text-conteudo-suave">—</span>
        </Dica>
      )
  }
}

/**
 * Variação sobre o período anterior.
 *
 * A cor vem de `comparacao.favoravel`, calculado no servidor — **não** do sinal do número.
 * É o que faz "CPL ↓ 18%" ser verde e "CTR ↓ 18%" ser vermelho sem a tela precisar saber,
 * métrica por métrica, se descer é bom.
 */
export function VariacaoPeriodo({ comparacao }: { readonly comparacao: Comparacao }) {
  if (comparacao.variacaoPercentual === null) {
    return (
      <Dica texto="Sem base de comparação: o período anterior não teve movimento nesta métrica.">
        <span className="text-xs text-conteudo-suave">sem comparação</span>
      </Dica>
    )
  }

  const variacao = comparacao.variacaoPercentual
  const cor =
    comparacao.favoravel === null
      ? 'text-conteudo-suave'
      : comparacao.favoravel
        ? 'text-sucesso'
        : 'text-perigo'

  return (
    <span className={`text-xs ${cor}`}>
      {variacao > 0 ? '↑' : variacao < 0 ? '↓' : '='} {Math.abs(variacao).toFixed(1)}%
      <span className="text-conteudo-suave"> vs. período anterior</span>
    </span>
  )
}

// ---------------------------------------------------------------------------
// Cartões
// ---------------------------------------------------------------------------

export function CartaoDeMetrica({ cartao }: { readonly cartao: CartaoMetrica }) {
  return (
    <Cartao>
        <div className="flex items-start justify-between gap-2">
          <p className="text-xs uppercase tracking-wide text-conteudo-suave">{cartao.rotulo}</p>
          {cartao.ressalva !== null ? (
            <Dica texto={cartao.ressalva}>
              <IconeAlerta className="h-4 w-4 text-atencao" />
            </Dica>
          ) : null}
        </div>
        <p className="mt-1 text-2xl font-semibold tabular-nums text-conteudo">
          {formatarMetrica(cartao.valor, cartao.formato)}
        </p>
        <div className="mt-1">
          <VariacaoPeriodo comparacao={cartao.comparacao} />
        </div>
    </Cartao>
  )
}

/**
 * Saúde da conta com as cinco dimensões — §27.
 *
 * As parcelas aparecem **expandidas**, ao contrário do score de anúncio: são sempre cinco,
 * e é o número que o gestor olha primeiro ao abrir a tela. Esconder a explicação atrás de
 * uma dica faria "91/100" ser um número sem justificativa, que é exatamente o que o §7
 * proíbe.
 */
export function PainelSaude({ saude }: { readonly saude: ResultadoSaude }) {
  const tom = TOM_POR_FAIXA[saude.faixa]

  return (
    <Cartao>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-conteudo-suave">Saúde da conta</p>
            <p className="mt-1 flex items-baseline gap-2">
              <span className="text-3xl font-semibold tabular-nums text-conteudo">
                {saude.avaliavel ? saude.nota : '—'}
              </span>
              {saude.avaliavel ? (
                <span className="text-sm text-conteudo-suave">/100</span>
              ) : null}
            </p>
          </div>
          <Etiqueta tom={tom}>{saude.rotulo}</Etiqueta>
        </div>

        {saude.parcelas.length > 0 ? (
          <ul className="mt-3 space-y-1 border-t border-borda pt-3">
            {saude.parcelas.map((parcela) => (
              <li key={parcela.dimensao} className="flex items-baseline justify-between gap-3 text-sm">
                <span className="text-conteudo-suave">{parcela.motivo}</span>
                <span className="shrink-0 tabular-nums text-conteudo">{parcela.pontos}/20</span>
              </li>
            ))}
          </ul>
        ) : null}
    </Cartao>
  )
}

// ---------------------------------------------------------------------------
// Fadiga
// ---------------------------------------------------------------------------

/**
 * Grau de fadiga.
 *
 * A linguagem é cautelosa por exigência do §9 — "possível", "alta probabilidade", nunca
 * "há fadiga". O motivo não é diplomacia: frequência alta com CTR em queda é o padrão da
 * fadiga, mas também de leilão mais caro, sazonalidade e público pequeno saturando. Nenhum
 * desses se resolve refazendo a arte, e afirmar fadiga mandaria o gestor gastar a semana
 * produzindo criativo pelo motivo errado.
 */
export function BadgeFadiga({ fadiga }: { readonly fadiga: DiagnosticoFadiga }) {
  if (fadiga.grau === 'SEM_SINAL') {
    return <span className="text-xs text-conteudo-suave">—</span>
  }

  return (
    <Dica texto={fadiga.resumo}>
      <Etiqueta tom={fadiga.grau === 'PROVAVEL' ? 'perigo' : 'atencao'}>
        {fadiga.grau === 'PROVAVEL' ? 'Provável fadiga' : 'Possível fadiga'}
      </Etiqueta>
    </Dica>
  )
}

// ---------------------------------------------------------------------------
// Defasagem
// ---------------------------------------------------------------------------

/**
 * Aviso de defasagem — RNF-80, e aqui não é rodapé.
 *
 * Os números vêm de sincronização periódica. Sem este carimbo, o gestor compara com o
 * Gerenciador de Anúncios, vê diferença e conclui que o sistema está errado. Acima de duas
 * horas o aviso muda de tom, porque aí a divergência deixa de ser normal e passa a ser
 * sintoma.
 */
export function AvisoDefasagem({
  defasagemMinutos,
  atualizadoEm,
}: {
  readonly defasagemMinutos: number
  readonly atualizadoEm: string | undefined
}) {
  if (atualizadoEm === undefined) return null

  const atrasado = defasagemMinutos > 120
  const texto =
    defasagemMinutos <= 0
      ? 'Dados sincronizados agora.'
      : `Dados da Meta sincronizados há ${defasagemMinutos} min.`

  return (
    <p className={`text-xs ${atrasado ? 'text-atencao' : 'text-conteudo-suave'}`}>
      {texto}
      {atrasado
        ? ' A sincronização pode estar atrasada — confira em Contas se há erro registrado.'
        : ''}
    </p>
  )
}
